import { Input, InputProps, useMultiStyleConfig } from '@chakra-ui/react';
import { FieldValues, useFormContext } from 'react-hook-form';

import { SimpleField, SimpleXProps } from './SimpleField';

export type SimpleInputProps<FormSchema extends FieldValues = FieldValues> = SimpleXProps<FormSchema> &
  InputProps & {
    formatter?: (value: string) => string;
    inputProps?: InputProps;
  };

export const SimpleInput = <FormSchema extends FieldValues = FieldValues>({
  name,
  label,
  placeholder,
  helper,
  type = 'text',
  variant,
  formatter,
  maxLength,
  pattern,
  inputProps = {},
  ...rest
}: SimpleInputProps<FormSchema>) => {
  const { register, watch } = useFormContext();
  const formProps = register(name);
  // Same style config SimpleField resolves, read here for the `input` part. Only `variant` is
  // relevant to that part, so the field's validation state doesn't need threading through.
  // Applied via `sx` rather than spread props: `sx` is typed for a SystemStyleObject, and it
  // outranks the `unstyled` variant's own styles the way the previous inline props did. Every
  // current `inputProps` caller passes behaviour (autoComplete/onChange/onBlur), never a style
  // key, so `sx` winning over them is not observable.
  const styles = useMultiStyleConfig('SimpleField', { variant });

  const value = formatter ? formatter?.(watch(name)) : watch(name);

  return (
    <SimpleField name={name} helper={helper} label={label} variant={variant} {...rest}>
      <Input
        type={type}
        variant="unstyled"
        placeholder={placeholder}
        step={type === 'number' ? 'any' : undefined}
        sx={styles.input}
        {...formProps}
        value={value || ''}
        maxLength={maxLength}
        pattern={pattern}
        {...inputProps}
        // `inputProps` is spread after `formProps`, so a caller-supplied onBlur would
        // otherwise replace react-hook-form's and drop its touched/validation tracking.
        // Chain instead, so callers can react to blur without disabling the field.
        onBlur={(event) => {
          formProps.onBlur(event);
          inputProps.onBlur?.(event);
        }}
      />
    </SimpleField>
  );
};
